import React, { useState, useEffect } from 'react';
import { Input, Button, IconButton, SegmentedControl } from '@vkontakte/vkui';
import { Icon20ViewOutline, Icon20HideOutline } from '@vkontakte/icons';
import { login, requestLoginCode, register, fetchBranches } from '../../utils/authUtils';
import CodeStep from './CodeStep';
import PasswordReset from './PasswordReset';
import logo from '../../assets/images/logo_512.png';
import './LoginPanel.css';

const ALLOWED_EMAIL_DOMAIN = '@rcfh.rosleshoz.gov.ru';

const isAllowedEmail = (value) =>
  value && value.trim().toLowerCase().endsWith(ALLOWED_EMAIL_DOMAIN.toLowerCase());

// Форматирование номера телефона в маску +7 (___) ___-__-__
const formatPhone = (value) => {
  let digits = (value || '').replace(/\D/g, '');
  // Нормализуем ведущую 8 или 7 к коду 7
  if (digits.startsWith('8')) {
    digits = '7' + digits.slice(1);
  } else if (!digits.startsWith('7')) {
    digits = '7' + digits;
  }
  digits = digits.slice(0, 11); // 7 + 10 цифр
  const rest = digits.slice(1); // 10 цифр номера

  let result = '+7';
  if (rest.length > 0) result += ' (' + rest.slice(0, 3);
  if (rest.length >= 3) result += ')';
  if (rest.length > 3) result += ' ' + rest.slice(3, 6);
  if (rest.length > 6) result += '-' + rest.slice(6, 8);
  if (rest.length > 8) result += '-' + rest.slice(8, 10);
  return result;
};

// Способы входа: по паролю (по умолчанию) или по коду из письма, без пароля.
const LOGIN_METHODS = [
  { label: 'По паролю', value: 'password' },
  { label: 'По коду из письма', value: 'code' },
];

// Телефон считается заполненным, когда введены все 11 цифр
const isPhoneComplete = (value) => (value || '').replace(/\D/g, '').length === 11;

const LoginPanel = ({ onLoginSuccess }) => {
  const [loginMethod, setLoginMethod] = useState('password');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regBranchId, setRegBranchId] = useState('');
  const [regFullName, setRegFullName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  // Шаг с кодом из письма: { purpose: 'login' | 'register', challenge }
  const [codeStep, setCodeStep] = useState(null);
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    if (showRegisterForm && branches.length === 0) {
      setBranchesLoading(true);
      fetchBranches()
        .then((list) => {
          setBranches(list);
          if (list.length > 0) {
            setRegBranchId(String(list[0].id));
          }
        })
        .finally(() => setBranchesLoading(false));
    }
  }, [showRegisterForm]);

  const renderPasswordToggle = (visible, toggle) => (
    <IconButton
      hoverMode="opacity"
      label={visible ? 'Скрыть пароль' : 'Показать пароль'}
      onClick={() => toggle((v) => !v)}
      disabled={isLoading}
    >
      {visible ? <Icon20HideOutline /> : <Icon20ViewOutline />}
    </IconButton>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const byCode = loginMethod === 'code';
    if (!username.trim() || (!byCode && !password.trim())) {
      setError(byCode ? 'Укажите почту' : 'Пожалуйста, заполните все поля');
      setIsLoading(false);
      return;
    }
    if (!isAllowedEmail(username)) {
      setError(`Укажите корпоративную почту с доменом ${ALLOWED_EMAIL_DOMAIN}`);
      setIsLoading(false);
      return;
    }

    const result = byCode ? await requestLoginCode(username.trim()) : await login(username.trim(), password);

    setIsLoading(false);

    if (result.success && result.codeRequired) {
      setCodeStep({ purpose: 'login', email: username.trim(), challenge: result.challenge });
    } else if (result.success) {
      onLoginSuccess(result.data.user);
    } else {
      setError(result.error || 'Ошибка авторизации');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!regUsername.trim() || !regPassword.trim()) {
      setError('Заполните почту и пароль');
      return;
    }
    if (!isAllowedEmail(regUsername)) {
      setError(`Укажите корпоративную почту с доменом ${ALLOWED_EMAIL_DOMAIN}`);
      return;
    }
    if (!regFullName.trim()) {
      setError('Укажите ФИО');
      return;
    }
    if (!regPhone.trim()) {
      setError('Укажите номер телефона');
      return;
    }
    if (!isPhoneComplete(regPhone)) {
      setError('Введите номер телефона полностью');
      return;
    }
    if (!regBranchId) {
      setError('Выберите филиал');
      return;
    }
    setIsLoading(true);
    const result = await register(
      regUsername.trim(),
      regPassword,
      Number(regBranchId),
      regFullName.trim(),
      regPhone.trim()
    );
    setIsLoading(false);
    if (result.success && result.codeRequired) {
      setCodeStep({ purpose: 'register', email: regUsername.trim(), challenge: result.challenge });
    } else if (result.success) {
      onLoginSuccess(result.data.user);
    } else {
      setError(result.error || 'Ошибка регистрации');
    }
  };

  if (codeStep) {
    return (
      <div className="login-panel-container">
        <div className="login-panel">
          <CodeStep
            key={codeStep.challenge.challenge_id}
            purpose={codeStep.purpose}
            email={codeStep.email}
            challenge={codeStep.challenge}
            onSuccess={(data) => onLoginSuccess(data.user)}
            onBack={() => {
              setCodeStep(null);
              setError('');
            }}
          />
        </div>
      </div>
    );
  }

  if (showReset) {
    return (
      <div className="login-panel-container">
        <div className="login-panel">
          <PasswordReset
            initialEmail={username.trim()}
            onLoginSuccess={onLoginSuccess}
            onBack={() => {
              setShowReset(false);
              setError('');
            }}
          />
        </div>
      </div>
    );
  }

  if (showRegisterForm) {
    return (
      <div className="login-panel-container">
        <div className="login-panel">
          <div className="login-panel-header">
            <div className="login-panel-logo-container">
              <img src={logo} alt="Логотип" className="login-panel-logo" />
            </div>
            <h2 className="login-panel-title">Регистрация</h2>
            <p className="login-panel-subtitle">
              Придумайте логин и пароль для тестовой работы с системой.
            </p>
          </div>

          <form onSubmit={handleRegisterSubmit} className="login-panel-form">
            <div className="login-panel-field">
              <label htmlFor="reg-username" className="login-panel-label">
                Почта
              </label>
              <Input
                id="reg-username"
                type="email"
                value={regUsername}
                onChange={(e) => setRegUsername(e.target.value)}
                placeholder={`name${ALLOWED_EMAIL_DOMAIN}`}
                disabled={isLoading}
                className="login-panel-input"
              />
            </div>

            <div className="login-panel-field">
              <label htmlFor="reg-password" className="login-panel-label">
                Пароль
              </label>
              <Input
                id="reg-password"
                type={showRegPassword ? 'text' : 'password'}
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                placeholder="Введите пароль"
                disabled={isLoading}
                className="login-panel-input"
                after={renderPasswordToggle(showRegPassword, setShowRegPassword)}
              />
            </div>

            <div className="login-panel-field">
              <label htmlFor="reg-full-name" className="login-panel-label">
                ФИО
              </label>
              <Input
                id="reg-full-name"
                type="text"
                value={regFullName}
                onChange={(e) => setRegFullName(e.target.value)}
                placeholder="Иванов Иван Иванович"
                disabled={isLoading}
                className="login-panel-input"
              />
            </div>

            <div className="login-panel-field">
              <label htmlFor="reg-phone" className="login-panel-label">
                Номер телефона
              </label>
              <Input
                id="reg-phone"
                type="tel"
                value={regPhone}
                onChange={(e) => setRegPhone(formatPhone(e.target.value))}
                onFocus={() => {
                  if (!regPhone) setRegPhone('+7 (');
                }}
                placeholder="+7 (___) ___-__-__"
                disabled={isLoading}
                className="login-panel-input"
              />
            </div>

            <div className="login-panel-field">
              <label htmlFor="reg-branch" className="login-panel-label">
                Филиал
              </label>
              <select
                id="reg-branch"
                value={regBranchId}
                onChange={(e) => setRegBranchId(e.target.value)}
                disabled={isLoading || branchesLoading}
                className="login-panel-input"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                }}
              >
                <option value="">Выберите филиал</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="login-panel-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="l"
              mode="outline"
              appearance="neutral"
              disabled={isLoading || branchesLoading}
              className="login-panel-button"
              style={{ width: '100%' }}
            >
              {isLoading ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>

            <button
              type="button"
              onClick={() => {
                setShowRegisterForm(false);
                setError('');
              }}
              className="login-panel-link"
              style={{
                marginTop: '12px',
                background: 'none',
                border: 'none',
                color: '#6c757d',
                fontSize: '14px',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Вернуться к входу
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-panel-container">
      <div className="login-panel">
        <div className="login-panel-header">
          <div className="login-panel-logo-container">
            <img src={logo} alt="Логотип" className="login-panel-logo" />
          </div>
          <h2 className="login-panel-title">Авторизация</h2>
          <p className="login-panel-subtitle">Войдите в систему для продолжения</p>
        </div>

        <form onSubmit={handleSubmit} className="login-panel-form">
          <SegmentedControl
            size="m"
            name="login-method"
            options={LOGIN_METHODS}
            value={loginMethod}
            onChange={(value) => {
              setLoginMethod(value);
              setError('');
            }}
            disabled={isLoading}
            className="login-panel-method"
          />

          <div className="login-panel-field">
            <label htmlFor="username" className="login-panel-label">
              Почта
            </label>
            <Input
              id="username"
              type="email"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={`name${ALLOWED_EMAIL_DOMAIN}`}
              disabled={isLoading}
              className="login-panel-input"
            />
          </div>

          {loginMethod === 'password' ? (
            <div className="login-panel-field">
              <label htmlFor="password" className="login-panel-label">
                Пароль
              </label>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Введите пароль"
                disabled={isLoading}
                className="login-panel-input"
                after={renderPasswordToggle(showPassword, setShowPassword)}
              />
            </div>
          ) : (
            <p className="login-panel-method-hint">
              Пришлём на почту одноразовый код — пароль не понадобится.
            </p>
          )}

          {error && (
            <div className="login-panel-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            size="l"
            mode="outline"
            appearance="neutral"
            disabled={isLoading}
            className="login-panel-button"
            style={{ width: '100%' }}
          >
            {loginMethod === 'code'
              ? (isLoading ? 'Отправка...' : 'Получить код')
              : (isLoading ? 'Вход...' : 'Войти')}
          </Button>

          <button
            type="button"
            onClick={() => setShowRegisterForm(true)}
            className="login-panel-link"
            style={{
              marginTop: '2px',
              background: 'none',
              border: 'none',
              color: '#6c757d',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Зарегистрироваться
          </button>

          <button
            type="button"
            onClick={() => {
              setShowReset(true);
              setError('');
            }}
            className="login-panel-link"
            style={{
              marginTop: '-8px',
              background: 'none',
              border: 'none',
              color: '#6c757d',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Забыли пароль?
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPanel;

